import axios from "axios";

const normalizeApiBaseUrl = () => {
  const configuredUrl = process.env.REACT_APP_API_URL || "http://localhost:8080/api";

  if (typeof configuredUrl !== "string") {
    return "http://localhost:8080/api";
  }

  return configuredUrl.replace(/\/+$/, "") || "http://localhost:8080/api";
};

const API_BASE_URL = normalizeApiBaseUrl();
const TOKEN_KEY = "token";
const REFRESH_TOKEN_KEY = "refreshToken";

const httpClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000,
});

export const getStoredSession = () => ({
  token: localStorage.getItem(TOKEN_KEY) || "",
  refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY) || "",
});

export const persistSessionTokens = ({ token, refreshToken }) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  }

  if (typeof refreshToken === "string") {
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    } else {
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
  }
};

export const clearStoredSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem("user");
};

let isRefreshing = false;
let queuedRequests = [];

const flushQueuedRequests = (error, token) => {
  queuedRequests.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  queuedRequests = [];
};

httpClient.interceptors.request.use(
  (config) => {
    const { token } = getStoredSession();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

httpClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const payload = error?.response?.data;
    const originalRequest = error?.config;
    const isAuthRoute =
      typeof originalRequest?.url === "string" &&
      (/\/auth\/login$/.test(originalRequest.url) ||
        /\/auth\/refresh$/.test(originalRequest.url) ||
        /\/public\/register$/.test(originalRequest.url));

    if (status === 401 && originalRequest && !originalRequest._retry && !isAuthRoute) {
      const { refreshToken } = getStoredSession();

      if (!refreshToken) {
        clearStoredSession();
        window.dispatchEvent(new CustomEvent("app:unauthorized"));
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          queuedRequests.push({ resolve, reject });
        })
          .then((nextToken) => {
            originalRequest.headers.Authorization = `Bearer ${nextToken}`;
            return httpClient(originalRequest);
          })
          .catch((refreshError) => Promise.reject(refreshError));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshResponse = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          { refreshToken },
          { timeout: 20000 }
        );
        const refreshPayload = refreshResponse?.data || {};
        const nextToken = refreshPayload.token || refreshPayload.accessToken || refreshPayload.jwt;
        const nextRefreshToken =
          refreshPayload.refreshToken || refreshPayload.refresh_token || refreshPayload.newRefreshToken || refreshToken;

        if (!nextToken) {
          throw new Error("Token refresh non disponibile");
        }

        persistSessionTokens({ token: nextToken, refreshToken: nextRefreshToken });
        window.dispatchEvent(
          new CustomEvent("app:session-refreshed", {
            detail: { token: nextToken, refreshToken: nextRefreshToken },
          })
        );

        flushQueuedRequests(null, nextToken);
        originalRequest.headers.Authorization = `Bearer ${nextToken}`;
        return httpClient(originalRequest);
      } catch (refreshError) {
        flushQueuedRequests(refreshError, null);
        clearStoredSession();
        window.dispatchEvent(new CustomEvent("app:unauthorized"));
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    const message =
      payload?.message ||
      payload?.error ||
      error?.message ||
      "Si e verificato un errore imprevisto.";

    window.dispatchEvent(
      new CustomEvent("app:api-error", {
        detail: {
          status,
          message,
        },
      })
    );

    if (status === 401) {
      clearStoredSession();
      window.dispatchEvent(new CustomEvent("app:unauthorized"));
    }

    return Promise.reject(error);
  }
);

export const extractApiError = (error) => {
  const payload = error?.response?.data;
  return payload?.message || payload?.error || error?.message || "Errore API non gestito";
};

export default httpClient;
